import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { usersApi } from "../../../services/api";
import Icon from "../../../components/AppIcon";

const ProfileSettings = () => {
  const { user, updateUser } = useAuth();
  const [notification, setNotification] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [formData, setFormData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phoneNumber: user?.phoneNumber || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      await usersApi.update(user.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber || null,
      });

      updateUser({
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
      });
      setIsEditing(false);
      showNotification("Profile updated successfully");
    } catch (err) {
      showNotification(err.message || "Failed to update profile", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification("Passwords do not match", "error");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showNotification("Password must be at least 8 characters", "error");
      return;
    }

    setIsSaving(true);

    // TODO: API call to change password
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setIsChangingPassword(false);
    setIsSaving(false);
    showNotification("Password changed successfully");
  };

  return (
    <>
      <Helmet>
        <title>Profile Settings - Rocket Tronc</title>
      </Helmet>

      <div className="p-6 max-w-4xl mx-auto">
        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 alert ${
              notification.type === "error" ? "alert-error" : "alert-success"
            }`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading font-bold">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account information
          </p>
        </div>

        <div className="grid gap-6">
          {/* Profile Information */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold">Profile Information</h2>
              {!isEditing && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Icon name="Pencil" size={16} />
                  Edit
                </button>
              )}
            </div>
            <div className="card-content">
              {isEditing ? (
                <form onSubmit={handleSaveProfile} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">First Name</label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            firstName: e.target.value,
                          })
                        }
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Last Name</label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) =>
                          setFormData({ ...formData, lastName: e.target.value })
                        }
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="label">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        className="input bg-muted"
                        disabled
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Email cannot be changed
                      </p>
                    </div>
                    <div>
                      <label className="label">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phoneNumber: e.target.value,
                          })
                        }
                        className="input"
                        placeholder="07700 900000"
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setIsEditing(false);
                        setFormData({
                          firstName: user?.firstName || "",
                          lastName: user?.lastName || "",
                          email: user?.email || "",
                          phoneNumber: user?.phoneNumber || "",
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Full Name</dt>
                    <dd className="font-medium">{user?.fullName}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Email</dt>
                    <dd className="font-medium">{user?.email}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Phone</dt>
                    <dd className="font-medium">{user?.phoneNumber || "-"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">Role</dt>
                    <dd className="font-medium">
                      {user?.role?.replace(/([A-Z])/g, " $1").trim()}
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          </div>

          {/* Change Password */}
          <div className="card">
            <div className="card-header flex items-center justify-between">
              <h2 className="text-lg font-semibold">Password</h2>
              {!isChangingPassword && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => setIsChangingPassword(true)}
                >
                  <Icon name="Key" size={16} />
                  Change Password
                </button>
              )}
            </div>
            <div className="card-content">
              {isChangingPassword ? (
                <form
                  onSubmit={handleChangePassword}
                  className="space-y-4 max-w-md"
                >
                  <div>
                    <label className="label">Current Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            currentPassword: e.target.value,
                          })
                        }
                        className="input pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((p) => ({ ...p, current: !p.current }))
                        }
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        <Icon name={showPasswords.current ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label">New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            newPassword: e.target.value,
                          })
                        }
                        className="input pr-10"
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((p) => ({ ...p, new: !p.new }))
                        }
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        <Icon name={showPasswords.new ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="label">Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            confirmPassword: e.target.value,
                          })
                        }
                        className="input pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((p) => ({ ...p, confirm: !p.confirm }))
                        }
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                      >
                        <Icon name={showPasswords.confirm ? "EyeOff" : "Eye"} size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSaving}
                    >
                      {isSaving ? "Changing..." : "Change Password"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => {
                        setIsChangingPassword(false);
                        setPasswordData({
                          currentPassword: "",
                          newPassword: "",
                          confirmPassword: "",
                        });
                        setShowPasswords({ current: false, new: false, confirm: false });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-muted-foreground">
                  Use a strong password with at least 8 characters including
                  numbers and symbols.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileSettings;
